import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { Attachment, Emoji } from 'components/composer/shared/editor_action_buttons_new';
import {
  BoldItalicUnderlineControls,
  ControlsWrapper,
  ListControls,
} from 'components/composer/shared/editor_controls_new';
import { EditorContainer } from 'components/composer/shared/editor_new';
import { LinkButton } from 'components/composer/shared/link_button';
import AddTask from 'actions/composition/add_task';
import AssigneeSelector from 'components/composer/shared/assignee_selector';
import Composer, { Header, HeaderText } from './shared/composer_new';
import ComposerEditor from 'components/composer/shared/composer_editor';
import DatePicker from 'components/composer/shared/date_picker';
import EditTask from 'actions/composer/edit_task';
import FontColorControls from 'components/composer/shared/font_color_controls';
import TextAlignmentMenu from 'components/composer/shared/editor_alignment_controls_new';
import useComposer from 'components/composer/shared/use_composer';
import useEditor from 'components/composer/shared/use_editor';
import useRichTextEditorPlugins from 'components/text_editor_new/hooks/use_rich_text_editor_plugins';
import serializeHtmlExternal from 'components/text_editor_new/lib/serialize_html_external';

const LeftWrapper = styled.div`
  display: flex;
`;

const submitTextForCreateOrEdit = isEditing => (isEditing ? 'Save Task' : 'Add Task');
const headerTextForCreateOrEdit = isEditing => (isEditing ? 'Edit Task' : 'Create Task');
const taskForCreateOrEdit = isEditing => (isEditing ? EditTask : AddTask);

export function TaskComposer({ isEditing }) {
  const composerProps = useComposer({
    action: taskForCreateOrEdit(isEditing),
    options: { trimEmptyParagraphs: true, trimParagraphWhitespace: true },
  });
  const editorProps = useEditor('bodyHtml', composerProps, { serializer: serializeHtmlExternal });
  const editorRef = useRichTextEditorPlugins(composerProps, editorProps);

  return (
    <Composer
      defaultEditor={editorRef.current}
      defaultEditorId={editorProps.editorId}
      defaultOnChange={editorProps.onChange}
      includeAttachments
      initialHtml={editorProps.initialHtml}
      submitText={submitTextForCreateOrEdit(isEditing)}
      {...composerProps}
    >
      <Header>
        <LeftWrapper>
          <HeaderText>{headerTextForCreateOrEdit(isEditing)}</HeaderText>
          <DatePicker />
        </LeftWrapper>
        <AssigneeSelector />
      </Header>
      <ControlsWrapper>
        <BoldItalicUnderlineControls {...editorProps} />
        <FontColorControls {...editorProps} />
        <TextAlignmentMenu {...editorProps} />
        <ListControls {...editorProps} />
        <LinkButton />
        <Emoji {...editorProps} />
        <Attachment alwaysShow />
      </ControlsWrapper>
      <EditorContainer includeAttachments>
        <ComposerEditor placeholder="Type to enter task description" {...editorProps} />
      </EditorContainer>
    </Composer>
  );
}

TaskComposer.propTypes = {
  isEditing: PropTypes.bool,
};

export default React.memo(TaskComposer);
